import { API_BASE_URL } from "@/config";
import { getAuthHeaders } from "./authService";

export interface MixtapeCover {
  type: "dynamic" | "uploaded";
  color?: string;
  label?: string;
  icon?: string;
  font?: string;
  fontColor?: string;
}

export interface MixtapeSummary {
  id: string;
  title: string;
  description?: string | null;
  creator_id?: string;
  creator_display_name?: string | null;
  is_staff?: boolean;
  artwork_url?: string | null;
  cover?: MixtapeCover;
  cover_type?: "dynamic" | "uploaded";
  is_public: boolean;
  is_dynamic?: boolean;
  follower_count?: number;
  created_at?: string;
  updated_at?: string;
  bundle_type?: string;
}

export interface MixtapeTrack {
  track_id: string;
  position: number;
  added_at?: string;
  track?: Record<string, any> | null;
}

export interface MixtapeDetailResponse {
  mixtape: MixtapeSummary;
  tracks: MixtapeTrack[];
  page: number;
  limit: number;
  total: number;
}

export interface CreateMixtapePayload {
  title: string;
  description?: string;
  is_public?: boolean;
  cover_type?: "dynamic" | "uploaded";
  cover_config?: Partial<MixtapeCover> | null;
}

const withAuth = (init?: RequestInit): RequestInit => ({
  ...init,
  headers: {
    "Content-Type": "application/json",
    ...getAuthHeaders(),
    ...(init?.headers || {}),
  },
});

async function handle<T>(response: Response): Promise<T> {
  if (!response.ok) {
    try {
      const body = await response.json();
      const message = body?.error || body?.message || "Request failed";
      throw new Error(message);
    } catch (err) {
      if (err instanceof Error) throw err;
      throw new Error("Request failed");
    }
  }
  return response.json();
}

export const createMixtape = async (payload: CreateMixtapePayload) => {
  const response = await fetch(`${API_BASE_URL}/mixtapes`, withAuth({
    method: "POST",
    body: JSON.stringify(payload),
  }));
  return handle<{ mixtape: MixtapeSummary }>(response);
};

export const fetchMyMixtapes = async () => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/me`, withAuth());
  return handle<{ mixtapes: MixtapeSummary[] }>(response);
};

export const fetchFollowingMixtapes = async () => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/me/following`, withAuth());
  return handle<{ mixtapes: MixtapeSummary[] }>(response);
};

export const fetchPublicMixtapes = async (params: { q?: string; creatorId?: string; page?: number; limit?: number; sort?: string; order?: string } = {}) => {
  const query = new URLSearchParams();
  if (params.q) query.append("q", params.q);
  if (params.creatorId) query.append("creator_id", params.creatorId);
  if (params.page) query.append("page", params.page.toString());
  if (params.limit) query.append("limit", params.limit.toString());
  if (params.sort) query.append("sort", params.sort);
  if (params.order) query.append("order", params.order);
  const response = await fetch(`${API_BASE_URL}/mixtapes${query.size ? `?${query}` : ""}`);
  return handle<{ page: number; limit: number; total: number; mixtapes: MixtapeSummary[] }>(response);
};

export const fetchMixtapeDetail = async (id: string, options: { page?: number; limit?: number } = {}) => {
  const query = new URLSearchParams();
  if (options.page) query.append("page", options.page.toString());
  if (options.limit) query.append("limit", options.limit.toString());
  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}${query.size ? `?${query}` : ""}`, withAuth());
  return handle<MixtapeDetailResponse>(response);
};

export const previewMixtape = async (id: string) => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}/preview`);
  return handle<{ mixtape: MixtapeSummary; tracks: MixtapeTrack[]; total: number }>(response);
};

export const updateMixtape = async (id: string, payload: Partial<CreateMixtapePayload>) => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}`, withAuth({
    method: "PATCH",
    body: JSON.stringify(payload),
  }));
  return handle<{ mixtape: MixtapeSummary }>(response);
};

export const deleteMixtape = async (id: string) => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}`, withAuth({ method: "DELETE" }));
  return handle<{ success: boolean }>(response);
};

export const addTracksToMixtape = async (id: string, trackIds: string[]) => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}/tracks`, withAuth({
    method: "POST",
    body: JSON.stringify({ tracks: trackIds }),
  }));
  return handle<{ added: number }>(response);
};

export const removeTrackFromMixtape = async (id: string, trackId: string) => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}/tracks/${trackId}`, withAuth({ method: "DELETE" }));
  return handle<{ success: boolean }>(response);
};

export const reorderMixtapeTracks = async (id: string, orderedTrackIds: string[]) => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}/tracks/reorder`, withAuth({
    method: "PATCH",
    body: JSON.stringify({ orderedTrackIds }),
  }));
  return handle<{ success: boolean }>(response);
};

export const uploadMixtapeCover = async (id: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}/cover/upload`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });

  return handle<{ mixtape: MixtapeSummary }>(response);
};

export const followMixtape = async (id: string) => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}/follow`, withAuth({ method: "POST" }));
  return handle<{ success: boolean }>(response);
};

export const unfollowMixtape = async (id: string) => {
  const response = await fetch(`${API_BASE_URL}/mixtapes/${id}/follow`, withAuth({ method: "DELETE" }));
  return handle<{ success: boolean }>(response);
};

export const fetchMixtapeBundle = async (id: string) => {
  const response = await fetch(`${API_BASE_URL}/bundle/mixtape/${id}`, withAuth());
  return handle<{ bundle_type: string; playlist_id: string; tracks: any[] }>(response);
};
